import { Box, Tooltip } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import SearchInputWithDropdown from "components/common/SearchInputWithDropdown";
import type { SearchResultItem } from "components/common/SearchInputWithDropdown";
import IconButtonCTA from "components/common/IconButtonCTA";
import PersonChip from "components/common/PersonChip";
import type { MultiComment } from "api/zekay/comments";
import { SCreate, SClose } from "styles/StyledMUI";
import { colorTokens } from "styles/globalStyles";
import type { ExcursionGroupCardUser } from "../../../excursions/shared/utils/excursionPlanningTypes";

const chipSx = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  color: colorTokens.text.primary,
  maxWidth: "100%",
  "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
};

export type GroupUsersSectionProps = {
  users: ExcursionGroupCardUser[];
  groupColor: string;
  canEdit: boolean;
  userSearchActive: boolean;
  userSearchQuery: string;
  onUserSearchChange: (query: string) => void;
  onUserSearchFocus: () => void;
  onUserSearchClose?: () => void;
  filteredUsers: {
    id: number;
    name: string;
    first_name?: string;
    last_name?: string;
    qualification?: string;
    slotUnavailable?: boolean;
    slotConflictHint?: string;
  }[];
  onSelectUser: (user: {
    id: number;
    name: string;
    first_name?: string;
    last_name?: string;
    qualification?: string;
  }) => void;
  onRemoveUser: (userIndex: number) => void;
  onUserColorChange?: (userIndex: number, hex: string) => void;
  onUserCommentsChange?: (userIndex: number, comments: MultiComment[]) => void;
  maxUsersPerGroup?: number;
  bookingStatusMap?: Record<number, "pending" | "validated">;
};

export function GroupUsersSection({
  users,
  groupColor,
  canEdit,
  userSearchActive,
  userSearchQuery,
  onUserSearchChange,
  onUserSearchFocus,
  onUserSearchClose,
  filteredUsers,
  onSelectUser,
  onRemoveUser,
  onUserColorChange,
  onUserCommentsChange,
  maxUsersPerGroup,
  bookingStatusMap,
}: GroupUsersSectionProps) {
  const hasZeroLeadLimit = maxUsersPerGroup != null && maxUsersPerGroup <= 0;
  const canAddLead =
    !hasZeroLeadLimit && (maxUsersPerGroup == null || (users?.length ?? 0) < maxUsersPerGroup);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <DarkTypography level="body-xs" colorVariant="legend" sx={{ mb: 0.5 }}>
        Encadrants
      </DarkTypography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
        {(users ?? []).map((u, userIndex) => (
          <PersonChip
            key={u.id}
            firstName={u.first_name}
            lastName={u.last_name}
            qualification={u.qualification}
            variant="soft"
            size="sm"
            chipColor={u.color ?? groupColor}
            onRemove={canEdit ? () => onRemoveUser(userIndex) : undefined}
            confirmRemove={
              canEdit
                ? { title: "Retirer l'encadrant", message: "Retirer cet encadrant du groupe ?" }
                : undefined
            }
            colorPicker={
              canEdit && onUserColorChange
                ? {
                    value: u.color ?? groupColor,
                    onChange: (hex) => onUserColorChange(userIndex, hex),
                    ariaLabel: "Couleur encadrant",
                  }
                : undefined
            }
            comments={u.comments}
            onCommentsChange={
              canEdit && onUserCommentsChange
                ? (nextComments) => onUserCommentsChange(userIndex, nextComments)
                : undefined
            }
            bookingStatus={bookingStatusMap?.[u.id] ?? null}
            sx={chipSx}
          />
        ))}
        {canEdit && userSearchActive ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flex: { xs: "1 1 100%", sm: "1 1 140px" },
              minWidth: 0,
            }}
          >
            <SearchInputWithDropdown
              value={userSearchQuery}
              onChange={(q) => onUserSearchChange(q)}
              onFocus={onUserSearchFocus}
              placeholder="Rechercher un encadrant…"
              results={filteredUsers.map(
                (u): SearchResultItem => ({
                  id: u.id,
                  label: u.qualification ? `${u.name} - ${u.qualification}` : u.name,
                  disabled: u.slotUnavailable === true,
                  conflictHint: u.slotConflictHint,
                }),
              )}
              onSelect={(item) => {
                if (item.disabled) return;
                const u = filteredUsers.find((r) => r.id === item.id);
                if (u && !u.slotUnavailable) onSelectUser(u);
              }}
              maxResults={userSearchQuery.trim() === "" ? 200 : 10}
              size="sm"
            />
            {onUserSearchClose && (
              <IconButtonCTA
                size="sm"
                variant="plain"
                aria-label="Fermer la recherche"
                onClick={onUserSearchClose}
              >
                <SClose />
              </IconButtonCTA>
            )}
          </Box>
        ) : canEdit && !userSearchActive ? (
          <Tooltip
            title={hasZeroLeadLimit ? "Ajout désactivé: ce modèle limite les encadrants à 0." : ""}
            disableHoverListener={!hasZeroLeadLimit}
          >
            <span>
              <IconButtonCTA
                size="sm"
                variant="soft"
                aria-label="Ajouter un encadrant"
                ctaVariant="accent"
                onClick={onUserSearchFocus}
                disabled={!canAddLead}
                sx={{ flexShrink: 0 }}
              >
                <SCreate />
              </IconButtonCTA>
            </span>
          </Tooltip>
        ) : null}
        {!canEdit && (users?.length ?? 0) === 0 ? (
          <DarkTypography level="body-sm" colorVariant="muted">
            —
          </DarkTypography>
        ) : null}
      </Box>
    </Box>
  );
}
